<script setup>
import {computed, onMounted} from 'vue';
import {Head, Link} from '@inertiajs/vue3';
import {Check, Lock, RefreshCw, Info} from 'lucide-vue-next';
import GeneralLayout from "@/Layouts/GeneralLayout.vue";
import {track} from '@/Composables/useAnalytics.js';
import {formatMoney, periodLabel, perTestLabel, isPaidPlan} from '@/Components/Monetization/planFormat.js';

const props = defineProps({
    plans: {
        type: Array,
        required: true
    },
    currency: {
        type: String,
        default: 'USD'
    },
    isAuthenticated: {
        type: Boolean,
        default: false
    },
    isFreeUser: {
        type: Boolean,
        default: true
    },
    creditsRemaining: {
        type: Number,
        default: null
    },
    // Why the user was sent here, e.g. 'no_credits' from the paywall.
    reason: {
        type: String,
        default: null
    },
    discount: {
        type: Object,
        default: null
    },
    // One-time pack from SprintPackService::present(), or null when not on sale.
    sprintPack: {
        type: Object,
        default: null
    }
});

const paidPlans = computed(() => props.plans.filter(isPaidPlan));
const freePlan = computed(() => props.plans.find(plan => !isPaidPlan(plan)) ?? null);

const hasDiscount = (plan) => plan.listPrice && Number(plan.listPrice) > Number(plan.price);

const discountLabel = computed(() => {
    if (!props.discount) return null;
    return props.discount.percentage ? `${props.discount.percentage}% discount applied` : 'Discount applied';
});

const reasonBanner = computed(() => {
    if (props.reason !== 'no_credits') return null;
    return props.isFreeUser
        ? "You've used your free sessions. Pick a plan to keep practising. Your past reports stay available either way."
        : "You've used all your credits for this billing period.";
});

onMounted(() => {
    track('paywall_viewed', {reason: props.reason || 'pricing_page', location: 'pricing'});
});

const onPlanClick = (plan) => {
    track('upgrade_cta_clicked', {location: 'pricing', reason: props.reason, plan: plan.name});
};

const onSprintClick = () => {
    track('upgrade_cta_clicked', {location: 'pricing', reason: props.reason, plan: 'sprint_pack'});
};

const faqs = [
    {
        q: "What's a credit?",
        a: 'One credit is one AI speaking session: a 5-minute session for job interviews, work & everyday English or IELTS, or a full IELTS mock test with all three parts (about 30 minutes). Every session comes with a scored report. A credit is only used once you finish answering at least one part; if a session ends before that, nothing is charged.'
    },
    {
        q: 'Can I cancel?',
        a: "Yes, anytime from Billing. Your plan stays active until the end of the period you've already paid for, and you won't be charged again."
    },
    {
        q: 'Do unused credits roll over?',
        a: 'Yes. Unused credits stay on your account, and each renewal adds the plan\'s credits on top.'
    },
    {
        q: "What's in the report?",
        a: 'A score on the 1–9 scale for each speaking criterion (fluency & coherence, vocabulary, grammar, pronunciation), your transcript, and your mistakes with corrections. IELTS sessions show it as an estimated band score.'
    },
];
</script>

<template>
    <Head title="Pricing"/>

    <GeneralLayout>
        <div class="max-w-7xl px-4 py-8 sm:px-6 lg:px-8 mx-auto sm:py-16">
            <!-- Context banner (e.g. arrived from the paywall) -->
            <div v-if="reasonBanner" role="status"
                 class="max-w-3xl mx-auto mb-8 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                <Info class="h-5 w-5 shrink-0 text-amber-600" aria-hidden="true"/>
                <p>{{ reasonBanner }}</p>
            </div>

            <!-- Hero Section -->
            <div class="max-w-2xl mx-auto text-center mb-8 sm:mb-14">
                <h1 class="text-2xl sm:text-3xl font-bold text-gray-900 lg:text-4xl">Speak with confidence when it counts</h1>
                <p class="mt-4 text-base sm:text-lg text-gray-600">
                    Each plan includes a set number of AI speaking practice sessions, each with a full scored report.
                    Longer plans cost less per session.
                </p>
                <p v-if="isAuthenticated && creditsRemaining !== null" class="mt-3 text-sm text-gray-500">
                    You currently have {{ creditsRemaining }} {{ isFreeUser ? 'free session' : 'credit' }}{{ creditsRemaining === 1 ? '' : 's' }} left.
                </p>
            </div>

            <!-- Paid plans -->
            <div class="grid gap-6 sm:gap-8 md:grid-cols-2 lg:grid-cols-3 mx-auto max-w-6xl">
                <div
                    v-for="plan in paidPlans"
                    :key="plan.planId"
                    :class="[
                        'relative flex flex-col rounded-2xl bg-white p-6 sm:p-8 transition-all duration-200',
                        plan.featured
                            ? 'ring-2 ring-primary-500 shadow-xl lg:-translate-y-2'
                            : 'ring-1 ring-gray-200 hover:shadow-lg',
                    ]"
                >
                    <div v-if="plan.featured" class="absolute -top-3 left-0 right-0 mx-auto w-40 text-center">
                        <span class="inline-block px-4 py-1 bg-primary-600 text-white text-xs font-semibold tracking-wider uppercase rounded-full shadow-sm">
                            Recommended
                        </span>
                    </div>

                    <div :class="['mb-5', plan.featured ? 'mt-3' : '']">
                        <h2 class="text-xl font-bold text-gray-900">{{ plan.name }}</h2>
                        <p v-if="plan.description" class="mt-2 text-sm text-gray-500">{{ plan.description }}</p>
                    </div>

                    <!-- Price -->
                    <div class="mb-6">
                        <div class="flex items-baseline gap-1">
                            <span class="text-4xl sm:text-5xl font-bold text-gray-900">{{ formatMoney(plan.price, plan.currency) }}</span>
                            <span class="text-lg text-gray-500">/ {{ periodLabel(plan) }}</span>
                        </div>
                        <div v-if="hasDiscount(plan)" class="mt-1 text-sm">
                            <span class="line-through text-gray-400">{{ formatMoney(plan.listPrice, plan.currency) }}</span>
                            <span v-if="discountLabel" class="ml-2 text-green-700 font-medium">{{ discountLabel }}</span>
                        </div>
                        <p class="mt-3 text-base font-semibold text-gray-900">
                            {{ plan.creditsPerCycle }} practice sessions every {{ periodLabel(plan) }}
                        </p>
                        <div class="mt-1 flex flex-wrap items-center gap-2">
                            <span v-if="perTestLabel(plan)" class="text-sm text-gray-600">{{ perTestLabel(plan) }}</span>
                            <span v-if="plan.savingsPercent"
                                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Save {{ plan.savingsPercent }}% vs monthly
                            </span>
                        </div>
                    </div>

                    <!-- What you get -->
                    <ul class="space-y-3 mb-8 flex-grow text-sm text-gray-700">
                        <li class="flex items-start gap-3">
                            <Check class="h-5 w-5 flex-shrink-0 text-primary-500" aria-hidden="true"/>
                            {{ plan.creditsPerCycle }} AI speaking sessions (interview, work or IELTS)
                        </li>
                        <li class="flex items-start gap-3">
                            <Check class="h-5 w-5 flex-shrink-0 text-primary-500" aria-hidden="true"/>
                            Scored report for every session: four criteria, transcript and corrections
                        </li>
                        <li class="flex items-start gap-3">
                            <Check class="h-5 w-5 flex-shrink-0 text-primary-500" aria-hidden="true"/>
                            Renews every {{ periodLabel(plan) }}. Cancel anytime
                        </li>
                    </ul>

                    <!-- Action -->
                    <div class="mt-auto">
                        <Link v-if="!isAuthenticated"
                              :href="route('register')"
                              @click.capture="onPlanClick(plan)"
                              :class="[
                                  'w-full inline-flex justify-center items-center px-4 py-3 text-sm font-semibold rounded-lg transition-all duration-200',
                                  plan.featured
                                      ? 'bg-primary-600 text-white hover:bg-primary-700 shadow-sm'
                                      : 'bg-white text-primary-600 ring-2 ring-primary-600 hover:bg-primary-50'
                              ]">
                            Create a free account
                        </Link>
                        <a v-else-if="isFreeUser"
                           :href="route('subscription.checkout', { plan: plan.planId })"
                           @click="onPlanClick(plan)"
                           :class="[
                               'w-full inline-flex justify-center items-center px-4 py-3 text-sm font-semibold rounded-lg transition-all duration-200',
                               plan.featured
                                   ? 'bg-primary-600 text-white hover:bg-primary-700 shadow-sm'
                                   : 'bg-white text-primary-600 ring-2 ring-primary-600 hover:bg-primary-50'
                           ]">
                            Get {{ plan.creditsPerCycle }} sessions
                        </a>
                        <Link v-else
                              :href="route('subscription.billing')"
                              class="w-full inline-flex justify-center items-center px-4 py-3 text-sm font-semibold rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200">
                            Manage your plan
                        </Link>
                    </div>
                </div>
            </div>

            <!-- One-time pack -->
            <div v-if="sprintPack" class="mt-8 max-w-6xl mx-auto rounded-2xl bg-white ring-1 ring-gray-200 p-6 sm:p-8 flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                    <span class="inline-block rounded-full bg-gray-800 px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-white">
                        One-time · no subscription
                    </span>
                    <h2 class="mt-3 text-xl font-bold text-gray-900">{{ sprintPack.name }}</h2>
                    <p class="mt-1 text-sm text-gray-600">
                        {{ sprintPack.credits }} AI speaking sessions with full reports, for the weeks before your interview or exam.
                        Charged once and never renews.
                    </p>
                </div>
                <div class="shrink-0 md:text-right">
                    <div class="text-3xl font-bold text-gray-900">{{ formatMoney(sprintPack.price, sprintPack.currency) }}</div>
                    <div v-if="sprintPack.pricePerTest" class="text-sm text-gray-600">≈{{ formatMoney(sprintPack.pricePerTest, sprintPack.currency) }} per session</div>
                    <Link v-if="!isAuthenticated"
                          :href="route('register')"
                          @click.capture="onSprintClick"
                          class="mt-3 inline-flex justify-center items-center px-5 py-2.5 text-sm font-semibold rounded-lg bg-white text-primary-600 ring-2 ring-primary-600 hover:bg-primary-50">
                        Create a free account
                    </Link>
                    <a v-else
                       :href="route('subscription.sprint.checkout')"
                       @click="onSprintClick"
                       class="mt-3 inline-flex justify-center items-center px-5 py-2.5 text-sm font-semibold rounded-lg bg-white text-primary-600 ring-2 ring-primary-600 hover:bg-primary-50">
                        Pay {{ formatMoney(sprintPack.price, sprintPack.currency) }} once
                    </a>
                </div>
            </div>

            <!-- Free plan -->
            <div v-if="freePlan" class="mt-8 max-w-6xl mx-auto rounded-2xl bg-gray-50 ring-1 ring-gray-200 p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h2 class="text-lg font-semibold text-gray-900">{{ freePlan.name }}</h2>
                    <p class="text-sm text-gray-600">
                        {{ freePlan.creditsPerCycle }} free AI speaking sessions when you sign up, with the same full report. No card needed.
                    </p>
                </div>
                <Link v-if="!isAuthenticated" :href="route('register')"
                      class="inline-flex justify-center items-center px-4 py-2.5 text-sm font-semibold rounded-lg bg-white ring-1 ring-gray-300 text-gray-800 hover:bg-gray-100 whitespace-nowrap">
                    Try it free
                </Link>
                <span v-else-if="isFreeUser" class="text-sm font-medium text-gray-500 whitespace-nowrap">Your current plan</span>
            </div>

            <!-- Trust -->
            <div class="mt-12 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-gray-600">
                <span class="inline-flex items-center gap-2"><Lock class="h-4 w-4" aria-hidden="true"/> Secure payment by Stripe</span>
                <span class="inline-flex items-center gap-2"><RefreshCw class="h-4 w-4" aria-hidden="true"/> Cancel anytime</span>
                <span>Prices in {{ currency }}</span>
            </div>

            <!-- FAQ -->
            <div class="mt-16 max-w-3xl mx-auto">
                <h2 class="text-xl font-bold text-gray-900 mb-4">Questions</h2>
                <div class="divide-y divide-gray-200 rounded-xl bg-white ring-1 ring-gray-200">
                    <details v-for="faq in faqs" :key="faq.q" class="group p-5">
                        <summary class="cursor-pointer list-none flex items-center justify-between font-medium text-gray-900">
                            {{ faq.q }}
                            <span class="ml-4 text-gray-400 group-open:rotate-45 transition-transform" aria-hidden="true">+</span>
                        </summary>
                        <p class="mt-3 text-sm text-gray-600">{{ faq.a }}</p>
                    </details>
                </div>
            </div>
        </div>
    </GeneralLayout>
</template>
